import type { HistoricalTrade } from 'models/forms/AddonManagementFormResult';
import type { FieldMetadata } from 'models/forms/fields';
import type { NextWorkflowError } from 'models/NextWorkflowError';
import type { QuoteWarning } from 'models/QuoteWarning';
import { connect } from 'react-redux';
import type { QuoteFormError } from 'state/addonForm/addonForm.selector';
import { addonManagementFormSelectToggled } from 'state/addonManagementForm/addonManagementForm.action';
import { addonManagementChangeFormValueThunk } from 'state/addonManagementForm/addonManagementForm.thunks';
import { ExposedFormStatus } from 'state/forms/models';
import type { ProductGraph } from 'state/product-description/model';
import {
  getAddonManagementFormErrorsById,
  getAddonManagementFormFieldMetadata,
  getAddonManagementFormQuoteWarnings,
  getAddonManagementFormStatusById,
  getAddonManagementValidationErrors,
  getFormEnrichedData,
  getFormInputData,
  getFormPendingEnrichmentData,
  getHasTechnicalErrors,
  getHasTimeoutErrors,
  getHistoricalTrades,
  isAddonManagementFormReadOnly,
  isAddonManagementFormSelected,
} from 'state/selectors';
import { getAddonManagementFormResult } from 'state/selectors/common/addonManagementForm';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { Collection } from 'state/utils/types';

interface OwnProps {
  formId: string;
  productDescription: ProductGraph;
  isUpfrontFeeColumnHidden: boolean;
  isOfferPriceColumnHidden: boolean;
  isFixedAmountInPercentColumnHidden: boolean;
  isTradingPriceColumnHidden: boolean;
}

interface StateProps {
  fieldsMetadata: Readonly<Record<string, Readonly<FieldMetadata>>>;
  inputData: Readonly<Record<string, Readonly<unknown>>>;
  historicalTrades: readonly HistoricalTrade[];
  pendingEnrichmentData: Readonly<Record<string, Readonly<unknown>>>;
  enrichedData: Readonly<Record<string, Readonly<unknown>>>;
  resultData?: Collection<Readonly<unknown>>;
  isFormSelected: boolean;
  status?: ExposedFormStatus;
  isReadOnly: boolean;
  errors?: readonly NextWorkflowError[];
  warnings?: readonly QuoteWarning[];
  validationErrors: readonly QuoteFormError[];
  formJsonRepresentation?: string;
  hasTimeoutError: boolean;
  hasTechnicalError: boolean;
}

interface DispatchProps {
  setFieldValue: (key: string, value: unknown) => void;
  toggleCheckbox: () => void;
}

export type AddonManagementFormProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: AppState, { formId }: OwnProps): StateProps => {
  const fieldsMetadata = getAddonManagementFormFieldMetadata(state, formId);
  const inputData = getFormInputData(state, formId);
  const historicalTrades = getHistoricalTrades(state, formId);
  const pendingEnrichmentData = getFormPendingEnrichmentData(state, formId);
  const enrichedData = getFormEnrichedData(state, formId);
  const isFormSelected = isAddonManagementFormSelected(state, formId);
  const status = getAddonManagementFormStatusById(state, formId);
  const isReadOnly = isAddonManagementFormReadOnly(state, formId);
  const resultData = getAddonManagementFormResult(state, formId);
  const errors =
    status === 'Rejected' || status === 'UnknownError' ? getAddonManagementFormErrorsById(state, formId) : undefined;
  const warnings = status === 'Warning' ? getAddonManagementFormQuoteWarnings(state, formId) : undefined;
  const validationErrors = getAddonManagementValidationErrors(state, formId);
  const hasTechnicalError = getHasTechnicalErrors(state, formId);
  const hasTimeoutError = getHasTimeoutErrors(state, formId);
  const formJsonRepresentation = '';
  return {
    fieldsMetadata,
    inputData,
    historicalTrades,
    pendingEnrichmentData,
    enrichedData,
    isFormSelected,
    status,
    isReadOnly,
    resultData,
    errors,
    warnings,
    validationErrors,
    formJsonRepresentation,
    hasTimeoutError,
    hasTechnicalError,
  };
};

function mapDispatchToProps(dispatch: ThunkDispatch, { formId }: OwnProps): DispatchProps {
  return {
    setFieldValue: (key: string, value: unknown) => {
      dispatch(addonManagementChangeFormValueThunk(formId, key, value));
    },
    toggleCheckbox: () => {
      dispatch(addonManagementFormSelectToggled(formId));
    },
  };
}

export const connectAddonManagementForm = connect(mapStateToProps, mapDispatchToProps);
